/**
 * An app's Home tab (Slack's App Home), shown in the app's DM when "Home" is picked above the conversation: the
 * blocks the app published for me (core AppHomeViewModel), live. Opening it loads the tab and tells the app
 * (`app_home_opened`), which usually publishes a fresh view.
 */
import type { AppHomeViewModel, RichTextContext } from "@bundle/core";
import { useEffect } from "react";
import { useViewModel } from "../hooks/index.ts";
import { useT, useTr } from "../i18n/I18nProvider.tsx";
import { Banner, ScrollView, Spinner, Stack, Text } from "../primitives/index.ts";
import { BlockKit } from "./BlockKit.tsx";

export type AppHomePanelProps = {
  vm: AppHomeViewModel;
  ctx: RichTextContext;
  /** The app's name (the empty state). */
  appName: string;
  openUrl: (url: string) => void;
  onUserPress?: (userId: string) => void;
  onChannelPress?: (channelId: string) => void;
  testID: string;
};

export function AppHomePanel({ vm, ctx, appName, openUrl, onUserPress, onChannelPress, testID }: AppHomePanelProps) {
  const t = useT();
  const tr = useTr();
  const state = useViewModel(vm);
  useEffect(() => vm.open(), [vm]);
  const view = state.home?.view;
  return (
    <Stack grow>
    <ScrollView testID={testID}>
      <Stack gap="3" paddingX="5" paddingY="4">
        {state.notice === undefined ? null : <Banner tone="danger" message={tr(state.notice)} onDismiss={() => vm.clearNotice()} testID={`${testID}-notice`} />}
        {state.status === "loading" ? (
          <Stack align="center" padding="6">
            <Spinner accessibilityLabel={t("appHome.loading")} />
          </Stack>
        ) : view === undefined ? (
          <Text color="text.secondary" testID={`${testID}-empty`}>
            {t("appHome.empty", { app: state.home?.app.name ?? appName })}
          </Text>
        ) : (
          <BlockKit
            key={state.home?.viewId}
            blocks={view.blocks}
            ctx={ctx}
            values={state.values}
            container={{ homeAppId: vm.appId }}
            onValue={(blockId, actionId, value) => vm.setValue(blockId, actionId, value)}
            onButton={(blockId, button) => vm.press(blockId, button, openUrl)}
            onPick={(blockId, menu, value) => vm.pickOverflow(blockId, menu, value)}
            onEnter={(blockId, actionId) => vm.dispatchText(blockId, actionId)}
            onUserPress={onUserPress}
            onChannelPress={onChannelPress}
            testID={`${testID}-blocks`}
          />
        )}
      </Stack>
    </ScrollView>
    </Stack>
  );
}
